/**
 * The nearest police station to a place, from OpenStreetMap, for taking an
 * arrested player there: Nominatim's search first, then each Overpass server
 * in turn. A station found is remembered for nearby asks, so a second arrest
 * in the same town asks nothing. Undefined when none is found, or nothing
 * answers in time: the game then lets the player go where they are.
 */

import type { StationPlace } from '../core/sources/osmpolice.js';
import { nearestFound, nearestStation, nominatimUrl, policeUrl } from '../core/sources/osmpolice.js';
import { DEFAULT_BUILDING_TAG_OPTIONS } from './buildingTagService.js';

/** How far about the place to look, metres. */
const SEARCH = 25_000;
const TIMEOUT_MS = 6_000;
/** Answers kept, by place: a station found serves asks within `REUSE` metres of where it was asked from. */
const REUSE = 3_000;
const found: Array<{ lat: number; lon: number; station: StationPlace | undefined }> = [];

export async function findPoliceStation(lat: number, lon: number): Promise<StationPlace | undefined> {
  const cos = Math.cos((lat * Math.PI) / 180);
  const kept = found.find(f => Math.hypot(f.lat - lat, (f.lon - lon) * cos) * 111_320 < REUSE);
  if (kept !== undefined) return kept.station;
  const asks: Array<{ url: string; read: (json: unknown) => StationPlace | undefined }> = [
    { url: nominatimUrl(lat, lon, SEARCH), read: json => nearestFound(json, lat, lon) },
    ...DEFAULT_BUILDING_TAG_OPTIONS.endpoints.map(endpoint => ({ url: policeUrl(endpoint, lat, lon, SEARCH), read: (json: unknown) => nearestStation(json, lat, lon) })),
  ];
  for (const { url, read } of asks) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) continue;
      const station = read(await response.json());
      // None found by a search that answered is an answer; a server that did not is not.
      found.push({ lat, lon, station });
      return station;
    } catch {
      // Timed out or refused: ask the next.
    } finally {
      clearTimeout(timer);
    }
  }
  return undefined;
}
